/* eslint-disable @typescript-eslint/indent */
import HotelDetailsType from '@customTypes/hotel-details';
import { SearchState } from '@customTypes/search';
import { format, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { CheckoutExtractedItem, CheckoutInfo } from '@customTypes/checkout-info';
import { v4 as uuidv4 } from 'uuid';
import Product from '@customTypes/product';
import { HotelApiAlgolia, HotelApiProduct } from '@customTypes/hotel-api';
import AVAILABILITY from '@constants/product';
import useDoOnce from './useDoOnce';

const RELEVANT_PRODUCT_TYPE_NAMES: ReadonlyArray<string> = ['Day Pass', 'Spa Pass'];
const RELEVANT_PRODUCT_TYPES: ReadonlyArray<string> = ['day_pass', 'spa_pass'];

// Destination FB events:
// https://developers.facebook.com/docs/marketing-api/destination-ads/events
// Search
// ViewContent
// InitiateCheckout
// Purchase

// Add type declaration for fbq
declare global {
  interface Window {
    fbq?: (
      type: string,
      eventName: string,
      params?: Record<string, any>,
      options?: Record<string, any>,
    ) => void;
  }
}

/**
 * Configuration interface for extracting product IDs from different product types.
 *
 * This uses the Strategy Pattern to handle different product types uniformly while
 * allowing type-specific logic for filtering, relevance checking, and ID extraction.
 *
 * @template T The product type (e.g., Product, HotelApiProduct, CheckoutExtractedItem)
 *
 * @example
 * ```typescript
 * const config: ProductIdExtractorConfig<Product> = {
 *   shouldIncludeProduct: (product) => product.availability === 'available',
 *   isRelevantProductType: (product) => ['day_pass', 'spa'].includes(product.product_type),
 *   extractProductIds: (product) => [String(product.product_id)]
 * };
 *
 * const productIds = getProductIds(products, config);
 * ```
 */
interface ProductIdExtractorConfig<T> {
  /**
   * Determines if a product should be included in the final result.
   * Used for filtering (e.g., availability checks, date restrictions).
   */
  shouldIncludeProduct: (product: T) => boolean;

  /**
   * Checks if the product type is relevant for Facebook tracking.
   * Only products of relevant types (day_pass, spa) should be tracked.
   */
  isRelevantProductType: (product: T) => boolean;

  /**
   * Extracts product IDs from a single product.
   * Handles complex cases like time-slotted products that have child products.
   *
   * @returns Array of product ID strings
   */
  extractProductIds: (product: T) => string[];
}

/**
 * Generic utility function to extract unique product IDs from any product type.
 *
 * This replaces 4 separate getProductIds functions that had similar logic but worked
 * with different product types. The function uses a configuration object to handle
 * type-specific differences while maintaining consistent filtering and deduplication logic.
 *
 * **How it works:**
 * 1. Filters products using `shouldIncludeProduct` (e.g., availability check)
 * 2. For each valid product, checks if it's a relevant type using `isRelevantProductType`
 * 3. If relevant, extracts product IDs using `extractProductIds`
 * 4. Flattens all IDs and removes duplicates
 *
 * **Why this approach:**
 * - **DRY**: Eliminates ~60 lines of duplicated code
 * - **Type-safe**: TypeScript ensures config matches product type
 * - **Extensible**: Easy to add new product types with new configs
 * - **Maintainable**: Logic changes happen in one place
 *
 * @template T The product type to process
 * @param products Array of products to extract IDs from
 * @param config Configuration object defining how to handle this product type
 * @returns Array of unique product ID strings
 *
 * @example
 * ```typescript
 * // For hotel search results
 * const searchIds = getProductIds(hotel.products, hotelApiProductConfig);
 *
 * // For hotel detail page
 * const viewIds = getProductIds(hotelProducts, productConfig);
 *
 * // For checkout
 * const checkoutIds = getProductIds(checkoutInfo.extracted_items, checkoutExtractedItemConfig);
 * ```
 */
function getProductIds<T>(products: T[], config: ProductIdExtractorConfig<T>): string[] {
  const productIds = products.filter(config.shouldIncludeProduct).flatMap((product: T) => {
    if (config.isRelevantProductType(product)) {
      return config.extractProductIds(product);
    }
    return [];
  });

  // Return unique product IDs
  return Array.from(new Set(productIds));
}

/**
 * Configuration for extracting product IDs from HotelApiProduct objects.
 * Used in the Search tracker when processing Algolia hotel search results.
 *
 * - Filters by availability ('available' only)
 * - Checks product_type_name against RELEVANT_PRODUCT_TYPE_NAMES
 * - Simple ID extraction using product.id
 */
const hotelApiProductConfig: ProductIdExtractorConfig<HotelApiProduct> = {
  shouldIncludeProduct: (product) => product.availability === AVAILABILITY.AVAILABLE,
  isRelevantProductType: (product) =>
    RELEVANT_PRODUCT_TYPE_NAMES.includes(product.product_type_name),
  extractProductIds: (product) => [String(product.id)],
};

/**
 * Configuration for extracting product IDs from Product objects.
 * Used in the ViewContent tracker on hotel detail pages.
 *
 * - Filters by availability ('available' only)
 * - Checks product_type against RELEVANT_PRODUCT_TYPES
 * - Handles time-slotted products (extracts child product IDs)
 * - Falls back to main product_id for regular products
 */
const productConfig: ProductIdExtractorConfig<Product> = {
  shouldIncludeProduct: (product) => product.availability === AVAILABILITY.AVAILABLE,
  isRelevantProductType: (product) => RELEVANT_PRODUCT_TYPES.includes(product.product_type),
  extractProductIds: (product) => {
    // Check for time slotted products (they have children with individual IDs)
    if (product.is_product_time_group) {
      return product.children?.flatMap((child) => [String(child.product_id)]) ?? [];
    }
    // Regular product - use main product ID
    return [String(product.product_id)];
  },
};

/**
 * Configuration for extracting product IDs from CheckoutExtractedItem objects.
 * Used in both InitiateCheckout and Purchase trackers.
 *
 * - No availability filtering (checkout items are already "available")
 * - Checks product_type_tag against RELEVANT_PRODUCT_TYPES
 * - Simple ID extraction (checkout endpoint already flattens time-slotted products)
 */
const checkoutExtractedItemConfig: ProductIdExtractorConfig<CheckoutExtractedItem> = {
  shouldIncludeProduct: () => true, // No availability check needed for checkout items
  isRelevantProductType: (product) => RELEVANT_PRODUCT_TYPES.includes(product.product_type_tag),
  extractProductIds: (product) => [String(product.product_id)],
};

export function useFacebookSearchTracker() {
  const trackSearch = useCallback((searchState: SearchState, hotels?: HotelApiAlgolia[]) => {
    if (typeof window !== 'undefined' && window.fbq) {
      const allProductIds =
        hotels?.flatMap((hotel) => getProductIds(hotel.products, hotelApiProductConfig)) ?? [];

      window.fbq(
        'track',
        'Search',
        {
          city: searchState.city,
          region: searchState.state_code, // TODO: no abbreviations
          country: searchState.country_code, // TODO: no abbreviations
          content_type: 'destination',
          ...(searchState.date && searchState.date !== 'any'
            ? {
                travel_start: format(parseISO(searchState.date), 'yyyy-MM-dd'),
                travel_end: format(parseISO(searchState.date), 'yyyy-MM-dd'),
              }
            : {}),
          content_ids: allProductIds.length > 0 ? JSON.stringify(allProductIds) : [],
          search_string: searchState.search_term,
        },
        { eventID: uuidv4() },
      );
    }
  }, []);

  return trackSearch;
}

export function useFacebookViewContentTracker() {
  const trackViewContent = useCallback(
    (hotelDetails: HotelDetailsType, hotelProducts: Product[], date: Date) => {
      if (typeof window !== 'undefined' && window.fbq) {
        const productIds = getProductIds(hotelProducts, productConfig);

        window.fbq(
          'track',
          'ViewContent',
          {
            city: hotelDetails.city_name,
            content_ids: productIds.length > 0 ? JSON.stringify(productIds) : [],
            content_type: 'destination',
            country: hotelDetails.country_name,
            region: hotelDetails.state_name,
            travel_start: format(date, 'yyyy-MM-dd'),
            travel_end: format(date, 'yyyy-MM-dd'),
          },
          { eventID: uuidv4() },
        );
      }
    },
    [],
  );

  return useDoOnce(trackViewContent);
}

export function useFacebookInitiateCheckoutTracker() {
  const trackInitiateCheckout = useCallback(
    (checkoutInfo: CheckoutInfo, hotelDetails: HotelDetailsType) => {
      if (typeof window !== 'undefined' && window.fbq) {
        const productIds = getProductIds(checkoutInfo.extracted_items, checkoutExtractedItemConfig);

        window.fbq(
          'track',
          'InitiateCheckout',
          {
            city: hotelDetails.city_name,
            content_ids: productIds.length > 0 ? JSON.stringify(productIds) : [],
            content_type: 'destination',
            country: hotelDetails.country_name,
            region: hotelDetails.state_name,
            travel_start: checkoutInfo.booked_date,
            travel_end: checkoutInfo.booked_date,
            num_adults: checkoutInfo.items[0].no_of_adults,
            num_children: checkoutInfo.items[0].no_of_childs,
          },
          { eventID: uuidv4() },
        );
      }
    },
    [],
  );

  return useDoOnce(trackInitiateCheckout);
}

export function useFacebookPurchaseTracker() {
  const trackPurchase = useCallback(
    (checkoutInfo: CheckoutInfo, hotelDetails: HotelDetailsType) => {
      if (typeof window !== 'undefined' && window.fbq) {
        const productIds = getProductIds(checkoutInfo.extracted_items, checkoutExtractedItemConfig);

        window.fbq(
          'track',
          'Purchase',
          {
            city: hotelDetails.city_name,
            content_ids: productIds.length > 0 ? JSON.stringify(productIds) : [],
            content_type: 'destination',
            country: hotelDetails.country_name,
            region: hotelDetails.state_name,
            travel_start: checkoutInfo.booked_date,
            travel_end: checkoutInfo.booked_date,
            num_adults: checkoutInfo.items[0].no_of_adults,
            num_children: checkoutInfo.items[0].no_of_childs,
            value: checkoutInfo.total,
            currency: 'USD',
          },
          { eventID: uuidv4() },
        );
      }
    },
    [],
  );

  return useDoOnce(trackPurchase);
}
